/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ApiRequestPendingInterface,
  RequestPendingApiResponseInterface,
  RequestPendingInitialProperties,
} from '../../interface/request-pending-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import penaltyManagementApi from '../../utils/api/penalty-management';

export const fetchRequestPendingStation = createAsyncThunk<
  RequestPendingApiResponseInterface, // Return type
  ApiRequestPendingInterface
>(
  'request-pending-station/fetchRequestPendingStation',
  async (data: ApiRequestPendingInterface, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.fetchRequestStationByStatus(
        removeEmptyKeys({ ...data, requestStatus: 'pending' }),
      );
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchRequestPendingStationFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'request-pending-station/fetchPendingStationFilters',
  async (_, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.fetchRequestStationFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: RequestPendingInitialProperties = {
  requestPending: [],
  limit: 0,
  page: 0,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const requestPendingStationSlice = createSlice({
  name: 'request-pending-station',
  initialState,
  reducers: {
    /**
     * Update the pending station filters
     * @param {any} state - The current state of the request pending station slice
     * @param {any} action - The payload action containing the filter update properties for pending station
     */
    updateRequestPendingStationFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state of the request pending station slice
     */
    disableRequestPendingStationFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Function to change sortBy
     * @param {any} state - The current state of the request pending station slice
     * @param {any} action - The payload action containing the sort field and sort order for pending station
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Function to handle async actions
   * @param {any} builder - The action reducer map builder for the request pending station slice
   */
  extraReducers: (builder: ActionReducerMapBuilder<RequestPendingInitialProperties>) => {
    builder
      .addCase(fetchRequestPendingStation.pending, (state: RequestPendingInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchRequestPendingStation.fulfilled,
        (
          state: RequestPendingInitialProperties,
          action: PayloadAction<RequestPendingApiResponseInterface>,
        ) => {
          state.loading = false;
          state.requestPending = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchRequestPendingStation.rejected, (state: RequestPendingInitialProperties) => {
        state.loading = false;
      });
    builder
      .addCase(
        fetchRequestPendingStationFilters.pending,
        (state: RequestPendingInitialProperties) => {
          state.filterListLoading = true;
        },
      )
      .addCase(
        fetchRequestPendingStationFilters.fulfilled,
        (
          state: RequestPendingInitialProperties,
          action: PayloadAction<CommonFilterProperties[]>,
        ) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...(action.payload || [])];
        },
      )
      .addCase(
        fetchRequestPendingStationFilters.rejected,
        (state: RequestPendingInitialProperties) => {
          state.filterListLoading = false;
        },
      );
  },
});
export const {
  updateRequestPendingStationFilters,
  disableRequestPendingStationFilterApplied,
  setSortData,
} = requestPendingStationSlice.actions;
export default requestPendingStationSlice.reducer;
